// ACHIEVEMENTS badges (2026-11-02): original SVG emblems drawn from a frame (by category), a metal tier (by reward) and a
// glyph. The art partner may paint badges later: list the id in BADGE_ART (src/app/iconArt.ts) and drop
// public/icons/badges/<id>.webp; until then (and if the file fails to load) the emblem below is shown. No DOM access here
// beyond building strings, so it is safe to import from tests.
import { BADGE_ART } from './iconArt';

export type BadgeFrame = 'shield' | 'round' | 'hex' | 'star' | 'diamond' | 'octa' | 'secret' | 'crest';
/** 0 bronze · 1 silver · 2 gold · 3 prism */
export type BadgeTier = 0 | 1 | 2 | 3;
export type Glyph =
  | 'hourglass' | 'crown' | 'blade' | 'burst' | 'flame' | 'moon' | 'eye' | 'orbit' | 'gem' | 'key' | 'compass' | 'book'
  | 'bolt' | 'infinity' | 'claws' | 'target' | 'chest' | 'fuse' | 'chevrons' | 'grid' | 'clock' | 'sun' | 'spiral' | 'heart'
  | 'star' | 'pin' | 'seven' | 'fast' | 'shield' | 'nocross' | 'swarm' | 'tower' | 'dots3' | 'one' | 'two' | 'wave';
export interface BadgeSpec { frame: BadgeFrame; tier: BadgeTier; glyph: Glyph }

const TIER: Record<BadgeTier, [string, string, string]> = { // light, dark, rim
  0: ['#f0b072', '#8a4a1e', '#ffd9b0'],
  1: ['#eef4fb', '#6f7f98', '#ffffff'],
  2: ['#ffe680', '#b8700c', '#fff4c0'],
  3: ['#c9a2ff', '#2b9fe0', '#f0e6ff'],
};
export const TIER_NAME: Record<BadgeTier, string> = { 0: 'bronze', 1: 'silver', 2: 'gold', 3: 'prism' };

const FRAME: Record<BadgeFrame, string> = {
  shield: 'M32 3 L56 11 V30 C56 45 45 55 32 61 C19 55 8 45 8 30 V11 Z',
  round: 'M32 3 A29 29 0 1 1 31.99 3 Z',
  hex: 'M32 3 L57 17.5 V46.5 L32 61 L7 46.5 V17.5 Z',
  star: 'M32 2 L39 12 L51 9 L50 21 L61 27 L53 36 L58 48 L46 49 L41 60 L32 53 L23 60 L18 49 L6 48 L11 36 L3 27 L14 21 L13 9 L25 12 Z',
  diamond: 'M32 2 L62 32 L32 62 L2 32 Z',
  octa: 'M21 3 H43 L61 21 V43 L43 61 H21 L3 43 V21 Z',
  secret: 'M32 3 A29 29 0 1 1 31.99 3 Z',
  crest: 'M8 8 H56 V34 C56 48 44 56 32 62 C20 56 8 48 8 34 Z',
};

/** glyphs drawn in the 64×64 box, centred on (32, 32); s = stroke-only paths, f = filled paths */
const GLYPH: Record<Glyph, { s?: string; f?: string }> = {
  hourglass: { s: 'M23 17h18M23 47h18M25 17c0 9 14 10 14 15s-14 6-14 15M39 17c0 9-14 10-14 15s14 6 14 15' },
  crown: { f: 'M19 42l-2-19 9 8 6-12 6 12 9-8-2 19zM19 45h26v4H19z' },
  blade: { f: 'M32 13l4 6v19h-8V19z', s: 'M24 41h16M32 41v9' },
  burst: { f: 'M32 14l4 11 11-4-5 10 10 5-11 3 3 11-10-6-6 10-3-11-11 3 6-10-10-5 11-3-4-10 11 4z' },
  flame: { f: 'M32 13c7 9 12 14 12 22a12 12 0 0 1-24 0c0-6 3-9 6-13 1 5 3 7 6 7-3-6-2-11 0-16z' },
  moon: { f: 'M37 15a17 17 0 1 0 10 28 14 14 0 1 1-10-28z' },
  eye: { s: 'M15 32c7-10 27-10 34 0-7 10-27 10-34 0z', f: 'M32 26a6 6 0 1 1-.01 0z' },
  orbit: { s: 'M32 20c10 0 18 5 18 12s-8 12-18 12-18-5-18-12 8-12 18-12zM20 22c6-6 16-2 20 6s4 16-2 18', f: 'M32 28a4 4 0 1 1-.01 0z' },
  gem: { f: 'M24 20h16l7 9-15 18-15-18z', s: 'M17 29h30M28 20l4 27M36 20l-4 27' },
  key: { s: 'M27 22a7 7 0 1 1-.01 0zM31 33l13 13M39 41l4-4M43 45l3-3' },
  compass: { s: 'M32 15a17 17 0 1 1-.01 0z', f: 'M32 20l4 12-4 12-4-12z' },
  book: { s: 'M18 20c5-2 10-2 14 2v24c-4-4-9-4-14-2zM46 20c-5-2-10-2-14 2v24c4-4 9-4 14-2z' },
  bolt: { f: 'M35 12L21 35h10l-4 17 16-24H33z' },
  infinity: { s: 'M32 32c-4-6-8-9-12-9a9 9 0 0 0 0 18c4 0 8-3 12-9s8-9 12-9a9 9 0 0 1 0 18c-4 0-8-3-12-9z' },
  claws: { s: 'M21 18c3 8 4 17 2 28M31 15c3 9 4 20 2 33M41 18c3 8 4 17 2 28' },
  target: { s: 'M32 16a16 16 0 1 1-.01 0zM32 24a8 8 0 1 1-.01 0zM32 10v10M32 44v10M10 32h10M44 32h10' },
  chest: { s: 'M17 30h30v16H17zM17 30c0-9 6-12 15-12s15 3 15 12M17 30h30', f: 'M29 33h6v6h-6z' },
  fuse: { s: 'M26 20a12 12 0 1 1-.01 0zM38 20a12 12 0 1 1-.01 0z', f: 'M32 22.5a12 12 0 0 1 0 19 12 12 0 0 1 0-19z' },
  chevrons: { s: 'M20 37l12-11 12 11M20 47l12-11 12 11M20 27l12-11 12 11' },
  grid: { f: 'M19 20h11v9H19zM34 20h11v9H34zM19 33h11v9H19zM34 33h11v9H34zM19 46h11v3H19zM34 46h11v3H34z' },
  clock: { s: 'M32 15a17 17 0 1 1-.01 0zM32 21v11l8 6' },
  sun: { f: 'M32 24a8 8 0 1 1-.01 0z', s: 'M32 12v6M32 46v6M12 32h6M46 32h6M18 18l4 4M42 42l4 4M46 18l-4 4M22 42l-4 4' },
  spiral: { s: 'M32 32c0-2 3-3 4-1s-1 6-5 6-7-4-6-8 6-8 11-7 10 6 9 12-7 11-14 11-13-6-13-13 6-15 15-15' },
  heart: { f: 'M32 47C20 38 15 32 15 25a8 8 0 0 1 17-3 8 8 0 0 1 17 3c0 7-5 13-17 22z', s: 'M33 22l-4 7 5 4-4 7' },
  star: { f: 'M32 13l5.6 12 13 1.4-9.8 8.8 2.8 12.8L32 41.6 20.4 48l2.8-12.8-9.8-8.8 13-1.4z' },
  pin: { f: 'M32 13a11 11 0 0 1 11 11c0 9-11 24-11 24S21 33 21 24a11 11 0 0 1 11-11z', s: 'M32 20a4 4 0 1 1-.01 0z' },
  seven: { f: 'M32 15a4 4 0 1 1-.01 0zM32 28a4 4 0 1 1-.01 0zM32 41a4 4 0 1 1-.01 0zM21 21.5a4 4 0 1 1-.01 0zM43 21.5a4 4 0 1 1-.01 0zM21 34.5a4 4 0 1 1-.01 0zM43 34.5a4 4 0 1 1-.01 0z' },
  fast: { s: 'M18 20l12 12-12 12M31 20l12 12-12 12', f: 'M44 29h4v6h-4z' },
  shield: { f: 'M32 14l14 5v11c0 9-6 15-14 19-8-4-14-10-14-19V19z' },
  nocross: { f: 'M28 18h8v10h10v8H36v10h-8V36H18v-8h10z', s: 'M17 47L47 17' },
  swarm: { f: 'M22 22a3 3 0 1 1-.01 0zM32 18a3 3 0 1 1-.01 0zM42 22a3 3 0 1 1-.01 0zM18 32a3 3 0 1 1-.01 0zM28 30a3 3 0 1 1-.01 0zM38 31a3 3 0 1 1-.01 0zM46 33a3 3 0 1 1-.01 0zM24 41a3 3 0 1 1-.01 0zM34 42a3 3 0 1 1-.01 0zM43 43a3 3 0 1 1-.01 0z' },
  tower: { f: 'M24 48V24l-3-3v-6h5v4h4v-4h4v4h4v-4h5v6l-3 3v24z' },
  dots3: { f: 'M20 29a3.5 3.5 0 1 1-.01 0zM32 29a3.5 3.5 0 1 1-.01 0zM44 29a3.5 3.5 0 1 1-.01 0z' },
  one: { f: 'M30 16h6v32h-6zM24 22l6-6v6z' },
  two: { s: 'M22 24c0-6 5-9 10-9s10 3 10 9c0 8-20 14-20 24h20' },
  wave: { s: 'M14 26c4-5 8-5 12 0s8 5 12 0 8-5 12 0M14 38c4-5 8-5 12 0s8 5 12 0 8-5 12 0' },
};

let uid = 0;
/** the emblem as an inline SVG string (locked = muted, rendered dim by CSS too) */
export function badgeSvg(b: BadgeSpec, locked = false): string {
  const [lt, dk, rim] = locked ? ['#5c6478', '#2a2f3c', '#8890a4'] as const : TIER[b.tier];
  const id = `bdg${++uid}`, g = GLYPH[b.glyph];
  const ring = b.frame === 'secret' ? `<path d="M32 9 A23 23 0 1 1 31.99 9 Z" fill="none" stroke="${rim}" stroke-width="1.6" stroke-dasharray="3 3" opacity=".8"/>` : '';
  return `<svg class="bdg" viewBox="0 0 64 64" aria-hidden="true"><defs><linearGradient id="${id}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${lt}"/><stop offset="1" stop-color="${dk}"/></linearGradient></defs>`
    + `<path d="${FRAME[b.frame]}" fill="url(#${id})" stroke="${rim}" stroke-width="2.4" stroke-linejoin="round"/>${ring}`
    + `<g transform="translate(32 32) scale(.82) translate(-32 -32)">`
    + (g.f ? `<path d="${g.f}" fill="#fff" fill-opacity=".94" stroke="rgba(10,10,30,.55)" stroke-width="1.2" stroke-linejoin="round"/>` : '')
    + (g.s ? `<path d="${g.s}" fill="none" stroke="#fff" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"/>` : '')
    + '</g></svg>';
}

/** painted badge if the art partner delivered one (listed in BADGE_ART), else the SVG emblem; a failed load falls back too */
export function badgeHtml(id: string, b: BadgeSpec, locked = false): string {
  const fb = badgeSvg(b, locked);
  if (!BADGE_ART.has(id)) return fb;
  return `<img class="bdg art${locked ? ' locked' : ''}" src="${import.meta.env.BASE_URL}icons/badges/${id}.webp" alt="" draggable="false" onerror="this.outerHTML=this.dataset.fb" data-fb='${fb.replace(/'/g, '&#39;')}'>`;
}
